import {
  AccessibilityHelp,
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BlockQuote,
  Bold,
  Code,
  EditorConfig,
  Essentials,
  FileRepository,
  FullPage,
  GeneralHtmlSupport,
  Heading,
  HorizontalLine,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  Paragraph,
  PasteFromOffice,
  SelectAll,
  ShowBlocks,
  SourceEditing,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  Undo,
} from 'ckeditor5';

/**
 * Configuración compartida del editor CKEditor 5 usada por:
 *   - HtmlEditorComponent (visor inline)
 *   - CrearModificarAyudaComponent (CRUD admin, ckeditor por idioma)
 *   - DialogoExportarAyudasComponent (PDF)
 *
 */
export const CKEDITOR_PLUGINS = [
  AccessibilityHelp,
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BlockQuote,
  Bold,
  Code,
  Essentials,
  FullPage,
  GeneralHtmlSupport,
  Heading,
  HorizontalLine,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  Paragraph,
  PasteFromOffice,
  SelectAll,
  ShowBlocks,
  FileRepository,
  SourceEditing,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  Undo,
];

const CKEDITOR_TOOLBAR = {
  items: [
    'undo', 'redo', '|',
    'sourceEditing', 'showBlocks', '|',
    'heading', '|',
    'bold', 'italic', 'code', '|',
    'horizontalLine', 'link', 'insertImage', 'insertTable', 'blockQuote', '|',
    'alignment', '|',
    'bulletedList', 'numberedList', 'outdent', 'indent',
  ],
  shouldNotGroupWhenFull: true,
};

const CKEDITOR_HEADING = {
  options: [
    { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
    { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
    { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
    { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
    { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
    { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
    { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' },
  ],
};

const CKEDITOR_IMAGE_TOOLBAR = [
  'toggleImageCaption', 'imageTextAlternative', '|',
  'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|',
  'resizeImage',
];

const CKEDITOR_TABLE_TOOLBAR = [
  'tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties',
];

/**
 * Devuelve una configuración nueva del editor (objeto fresco por instancia para
 * evitar que CKEditor mute estado compartido entre editores).
 */
export function buildCkeditorConfig(placeholder = 'Escriba aquí'): EditorConfig {
  return {
    licenseKey: 'GPL',
    language: { ui: 'es', content: 'es' },
    toolbar: CKEDITOR_TOOLBAR,
    plugins: CKEDITOR_PLUGINS,
    heading: CKEDITOR_HEADING,
    htmlSupport: {
      allow: [
        { name: 'p', attributes: true, classes: false, styles: ['text-align'] },
        { name: 'h1', attributes: false, classes: false, styles: ['text-align'] },
        { name: 'h2', attributes: false, classes: false, styles: ['text-align'] },
        { name: 'h3', attributes: false, classes: false, styles: ['text-align'] },
        { name: 'h4', attributes: false, classes: false, styles: ['text-align'] },
        { name: 'h5', attributes: false, classes: false, styles: ['text-align'] },
        { name: 'h6', attributes: false, classes: false, styles: ['text-align'] },
        { name: 'strong', attributes: false, classes: false, styles: false },
        { name: 'em', attributes: false, classes: false, styles: false },
        { name: 'u', attributes: false, classes: false, styles: false },
        { name: 'a', attributes: ['href', 'target', 'rel', 'download'], classes: false, styles: false },
        { name: 'ul', attributes: false, classes: false, styles: false },
        { name: 'ol', attributes: ['start', 'type'], classes: false, styles: false },
        { name: 'li', attributes: false, classes: false, styles: false },
        { name: 'blockquote', attributes: false, classes: false, styles: false },
        { name: 'code', attributes: false, classes: false, styles: false },
        { name: 'hr', attributes: false, classes: false, styles: false },
        { name: 'br', attributes: false, classes: false, styles: false },
        { name: 'figure', attributes: true, classes: true, styles: ['width', 'text-align'] },
        { name: 'img', attributes: ['src', 'alt', 'width', 'height'], classes: true, styles: false },
        { name: 'table', attributes: true, classes: true, styles: false },
        { name: 'thead', attributes: false, classes: false, styles: false },
        { name: 'tbody', attributes: false, classes: false, styles: false },
        { name: 'tr', attributes: false, classes: false, styles: false },
        { name: 'th', attributes: ['colspan', 'rowspan'], classes: false, styles: ['width', 'text-align'] },
        { name: 'td', attributes: ['colspan', 'rowspan'], classes: false, styles: ['width', 'text-align'] },
        { name: 'colgroup', attributes: false, classes: false, styles: false },
        { name: 'col', attributes: ['width', 'span'], classes: false, styles: ['width'] },
        { name: 'caption', attributes: false, classes: false, styles: false },
        { name: 'span', attributes: false, classes: false, styles: false },
        { name: 'div', attributes: false, classes: false, styles: false },
      ],
    },
    image: { toolbar: CKEDITOR_IMAGE_TOOLBAR },
    link: {
      addTargetToExternalLinks: true,
      defaultProtocol: 'https://',
      decorators: {
        toggleDownloadable: {
          mode: 'manual',
          label: 'Downloadable',
          attributes: { download: 'file' },
        },
      },
    },
    list: {
      properties: { styles: true, startIndex: true, reversed: true },
    },
    placeholder,
    table: { contentToolbar: CKEDITOR_TABLE_TOOLBAR },
  } as EditorConfig;
}
